import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import StatusBadge from '@/components/base/StatusBadge';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { supabase } from '@/lib/supabaseClient';

// ── Types ─────────────────────────────────────────────────────────────────────
interface SaasPlan {
  id: string; name: string; price_monthly: number;
  max_users: number; max_contracts: number; storage_mb: number;
}

interface Tenant {
  id: string; name: string; slug: string; plan_code: string; status: string;
  storage_quota_mb: number; storage_used_mb: number;
  max_users: number; max_active_contracts: number; created_at: string;
}

interface TenantModule {
  id: string; tenant_id: string; module_id: string;
  is_enabled: boolean; price_monthly: number; source: string;
}

interface SaasModule {
  id: string; name: string; category: string;
  is_core: boolean; is_addon: boolean; price_monthly: number;
}

interface PlanModule { plan_id: string; module_id: string; is_included: boolean; limit_value: number | null; }

interface BillingRow {
  id: string; name: string; slug: string; plan_code: string; status: string;
  plan_price: number; addon_price: number; addon_count: number; total: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#C8A96E' };

const PLAN_BADGE: Record<string, string> = {
  free:       'bg-slate-100 text-slate-500',
  pro:        'bg-amber-50 text-amber-700',
  enterprise: 'bg-emerald-50 text-emerald-700',
};

const STATUS_ACTIONS: { status: string; label: string; next: string; icon: string; color: string }[] = [
  { status: 'active',    label: 'Suspender',  next: 'suspended', icon: 'ri-pause-circle-line',  color: 'text-amber-600 hover:bg-amber-50' },
  { status: 'trial',     label: 'Activar',    next: 'active',    icon: 'ri-checkbox-circle-line',  color: 'text-emerald-600 hover:bg-emerald-50' },
  { status: 'suspended', label: 'Reactivar',  next: 'active',    icon: 'ri-play-circle-line',   color: 'text-emerald-600 hover:bg-emerald-50' },
  { status: 'cancelled', label: 'Reactivar',  next: 'active',    icon: 'ri-play-circle-line',   color: 'text-emerald-600 hover:bg-emerald-50' },
];

const ACTIVE_STATUSES = new Set(['active', 'trial']);
type TabKey = 'overview' | 'tenants' | 'plan-modules';

// ── Addon Detail Modal ────────────────────────────────────────────────────────
function AddonDetailModal({
  tenant, modules, tenantModules, onClose,
}: {
  tenant: BillingRow;
  modules: SaasModule[];
  tenantModules: TenantModule[];
  onClose: () => void;
}) {
  const addonMods = tenantModules
    .filter((tm) => tm.tenant_id === tenant.id && tm.is_enabled && tm.price_monthly > 0)
    .map((tm) => {
      const mod = modules.find((m) => m.id === tm.module_id);
      return { ...tm, name: mod?.name ?? tm.module_id, category: mod?.category ?? '—' };
    });

  const included = tenantModules
    .filter((tm) => tm.tenant_id === tenant.id && tm.is_enabled && tm.price_monthly === 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl border border-slate-200 w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <p className="text-[#0f172a] font-semibold text-sm">{tenant.name}</p>
            <p className="text-slate-400 text-xs">Add-ons activos · {tenant.plan_code}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 cursor-pointer">
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Paid add-ons */}
          <div>
            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Add-ons de pago</p>
            {addonMods.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-4">Sin add-ons de pago</p>
            ) : (
              <div className="space-y-2">
                {addonMods.map((a) => (
                  <div key={a.id} className="flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-lg">
                        <i className="ri-puzzle-line text-amber-600 text-xs" />
                      </div>
                      <div>
                        <p className="text-slate-700 text-sm font-medium">{a.name}</p>
                        <p className="text-slate-400 text-[11px]">{a.category}</p>
                      </div>
                    </div>
                    <span className="text-amber-700 text-sm font-bold">${a.price_monthly}/mes</span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-2 rounded-lg bg-slate-50 border border-slate-100 mt-1">
                  <span className="text-slate-600 text-xs font-medium">Total add-ons</span>
                  <span className="text-slate-900 text-sm font-bold">${tenant.addon_price}/mes</span>
                </div>
              </div>
            )}
          </div>

          {/* Plan included */}
          <div>
            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Incluidos en plan</p>
            {included.length === 0 ? (
              <p className="text-slate-400 text-xs text-center py-2">—</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {included.map((tm) => {
                  const mod = modules.find((m) => m.id === tm.module_id);
                  return (
                    <span key={tm.id} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                      {mod?.name ?? tm.module_id}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Revenue summary */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Plan ({tenant.plan_code})</span>
                <span className="text-slate-700 font-medium">${tenant.plan_price}/mes</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Add-ons ({addonMods.length})</span>
                <span className="text-amber-600 font-medium">${tenant.addon_price}/mes</span>
              </div>
              <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-slate-200">
                <span className="text-slate-700">Total mensual</span>
                <span className="text-[#0f172a]">${tenant.total}/mes</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
          <Link
            to={`/sa/tenants/${tenant.id}`}
            className="flex items-center gap-2 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            onClick={onClose}
          >
            <i className="ri-external-link-line" />
            Ver tenant completo
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Plan Modules Matrix ────────────────────────────────────────────────────────
function PlanModulesMatrix({
  modules, plans, planModules, onRefresh,
}: {
  modules: SaasModule[];
  plans: SaasPlan[];
  planModules: PlanModule[];
  onRefresh: () => void;
}) {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [matrix, setMatrix]     = useState<Record<string, boolean>>({});

  const mKey = (planId: string, moduleId: string) => `${planId}__${moduleId}`;

  useEffect(() => {
    const mat: Record<string, boolean> = {};
    plans.forEach((p) => modules.forEach((m) => { mat[mKey(p.id, m.id)] = false; }));
    planModules.forEach((r) => { mat[mKey(r.plan_id, r.module_id)] = r.is_included; });
    setMatrix(mat);
  }, [modules, plans, planModules]);

  const toggle = (planId: string, moduleId: string) => {
    if (!editMode) return;
    const key = mKey(planId, moduleId);
    setMatrix((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setSaving(true);
    const upserts = plans.flatMap((p) =>
      modules.map((m) => ({
        plan_id: p.id,
        module_id: m.id,
        is_included: matrix[mKey(p.id, m.id)] ?? false,
        limit_value: null,
      }))
    );
    const { error } = await supabase
      .from('saas_plan_modules')
      .upsert(upserts, { onConflict: 'plan_id,module_id' });
    setSaving(false);
    if (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW });
      return;
    }
    setEditMode(false);
    onRefresh();
    Swal.fire({ icon: 'success', title: 'saas_plan_modules actualizado', timer: 1400, showConfirmButton: false, ...SW });
  };

  const cancel = () => { setEditMode(false); onRefresh(); };
  const categories = [...new Set(modules.map((m) => m.category))].sort();
  const planInclCount = (planId: string) => modules.filter((m) => matrix[mKey(planId, m.id)]).length;

  const CAT_STYLE: Record<string, { dot: string; label: string }> = {
    Production:  { dot: 'bg-emerald-400', label: 'text-emerald-600' },
    Website:     { dot: 'bg-cyan-400',    label: 'text-cyan-600' },
    Automation:  { dot: 'bg-amber-400',   label: 'text-amber-600' },
    Settings:    { dot: 'bg-rose-400',    label: 'text-rose-600' },
  };

  const MODULE_ICONS: Record<string, string> = {
    clients: 'ri-user-3-line', contracts: 'ri-file-text-line', payments: 'ri-bank-card-line',
    jobs: 'ri-briefcase-line', public_website: 'ri-global-line', client_portal: 'ri-user-shared-line',
    basic_branding: 'ri-palette-line', digital_invitations: 'ri-mail-send-line', rsvp: 'ri-calendar-check-line',
    media_packages: 'ri-folder-image-line', photo_selection: 'ri-image-line',
    final_deliveries: 'ri-send-plane-line', ai_sales_assistant: 'ri-robot-line', appointments: 'ri-calendar-line',
    whatsapp_notifications: 'ri-whatsapp-line', email_automations: 'ri-mail-settings-line',
    custom_domain: 'ri-earth-line', extra_storage: 'ri-hard-drive-line',
    signature_book: 'ri-book-line', leads: 'ri-user-received-line',
  };

  const PLAN_COLORS: Record<string, { text: string; bg: string; check: string }> = {
    free:       { text: 'text-slate-500',   bg: 'bg-slate-100',   check: 'text-slate-500' },
    pro:        { text: 'text-amber-700',   bg: 'bg-amber-50',    check: 'text-amber-600' },
    enterprise: { text: 'text-emerald-700', bg: 'bg-emerald-50',  check: 'text-emerald-600' },
  };

  const colTemplate = `grid-cols-[1fr_repeat(${plans.length},_120px)]`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-slate-700 text-sm font-semibold">Módulos por Plan</h3>
          <p className="text-slate-400 text-xs mt-0.5">Qué módulos incluye cada plan — se guarda en saas_plan_modules</p>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <button onClick={cancel} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-900 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-1.5 text-xs bg-[#C8A96E] hover:bg-[#b8965a] text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </>
          ) : (
            <button onClick={() => setEditMode(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-edit-line text-xs" />
              Editar
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Header row */}
        <div className={`grid ${colTemplate} border-b border-slate-200 bg-slate-50`}>
          <div className="px-5 py-4">
            <p className="text-slate-500 text-xs font-medium">Módulo</p>
          </div>
          {plans.map((p) => {
            const pc = PLAN_COLORS[p.id] ?? PLAN_COLORS.free;
            return (
              <div key={p.id} className="px-4 py-4 text-center border-l border-slate-200">
                <p className={`text-sm font-bold ${pc.text}`}>{p.name}</p>
                <p className="text-slate-400 text-xs">${Number(p.price_monthly)}/mes</p>
                <p className="text-slate-300 text-[10px] mt-1">{planInclCount(p.id)} módulos</p>
              </div>
            );
          })}
        </div>

        {categories.map((cat) => {
          const mods = modules.filter((m) => m.category === cat);
          if (mods.length === 0) return null;
          const cs = CAT_STYLE[cat] ?? { dot: 'bg-slate-300', label: 'text-slate-500' };
          return (
            <div key={cat}>
              {/* Category header */}
              <div className={`grid ${colTemplate} border-b border-slate-100 bg-slate-50/50`}>
                <div className="px-5 py-2.5 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${cs.dot}`} />
                  <span className={`text-[11px] font-semibold uppercase tracking-wider ${cs.label}`}>{cat}</span>
                </div>
                {plans.map((p) => <div key={p.id} className="border-l border-slate-100" />)}
              </div>

              {mods.map((mod, idx) => {
                const isLast = idx === mods.length - 1;
                const icon = MODULE_ICONS[mod.id] ?? 'ri-apps-line';
                return (
                  <div
                    key={mod.id}
                    className={`grid ${colTemplate} ${!isLast ? 'border-b border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}
                  >
                    <div className="px-5 py-3 flex items-center gap-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-slate-100 shrink-0">
                        <i className={`${icon} text-xs ${cs.label}`} />
                      </span>
                      <div>
                        <p className="text-slate-700 text-sm">{mod.name}</p>
                        {mod.is_core && <span className="text-[10px] text-emerald-600 font-medium">Core</span>}
                        {mod.price_monthly > 0 && (
                          <span className="text-[10px] text-amber-500 font-medium ml-2">+${mod.price_monthly}/mes</span>
                        )}
                      </div>
                    </div>
                    {plans.map((plan) => {
                      const key = mKey(plan.id, mod.id);
                      const included = matrix[key] ?? false;
                      const pc = PLAN_COLORS[plan.id] ?? PLAN_COLORS.free;
                      return (
                        <div
                          key={plan.id}
                          className={`border-l border-slate-100 flex items-center justify-center py-3 ${editMode ? 'cursor-pointer' : ''}`}
                          onClick={() => toggle(plan.id, mod.id)}
                        >
                          {included ? (
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full transition-all ${editMode ? `${pc.bg} ${pc.check} hover:opacity-70` : `${pc.bg} ${pc.check}`}`}>
                              <i className="ri-check-line text-xs" />
                            </span>
                          ) : (
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full transition-all ${editMode ? 'bg-slate-100 text-slate-300 hover:bg-slate-200' : 'text-slate-200'}`}>
                              <i className="ri-close-line text-xs" />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center rounded-full bg-[#C8A96E]/20 text-[#C8A96E]"><i className="ri-check-line text-[10px]" /></span>
            <span className="text-slate-400 text-xs">Incluido</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center rounded-full bg-slate-100 text-slate-300"><i className="ri-close-line text-[10px]" /></span>
            <span className="text-slate-400 text-xs">No incluido</span>
          </div>
          {editMode && (
            <span className="text-amber-600 text-xs ml-auto font-medium">
              Haz clic en las celdas para cambiar · Los cambios se sincronizan en saas_plan_modules
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function SABilling() {
  const navigate = useNavigate();

  const [plans, setPlans]               = useState<SaasPlan[]>([]);
  const [tenants, setTenants]           = useState<Tenant[]>([]);
  const [tenantModules, setTenantModules] = useState<TenantModule[]>([]);
  const [modules, setModules]           = useState<SaasModule[]>([]);
  const [planModules, setPlanModules]   = useState<PlanModule[]>([]);
  const [rows, setRows]                 = useState<BillingRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [tab, setTab]                   = useState<TabKey>('overview');

  // filters
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy]             = useState<'total' | 'name' | 'plan'>('total');

  // modal
  const [addonModal, setAddonModal]     = useState<BillingRow | null>(null);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [tenantsRes, plansRes, tenantModRes, modulesRes, planModRes] = await Promise.all([
      supabase.from('saas_tenants').select('*').order('created_at', { ascending: false }),
      supabase.from('saas_plans').select('*').order('price_monthly'),
      supabase.from('tenant_modules').select('id, tenant_id, module_id, is_enabled, price_monthly, source'),
      supabase.from('saas_modules').select('id, name, category, is_core, is_addon, price_monthly').order('category').order('name'),
      supabase.from('saas_plan_modules').select('plan_id, module_id, is_included, limit_value'),
    ]);

    const tenantList    = (tenantsRes.data  ?? []) as Tenant[];
    const planList      = (plansRes.data    ?? []) as SaasPlan[];
    const tenantModList = (tenantModRes.data ?? []) as TenantModule[];
    const moduleList    = (modulesRes.data  ?? []) as SaasModule[];
    const planModList   = (planModRes.data  ?? []) as PlanModule[];

    // Build plan price map from real data
    const planPriceMap: Record<string, number> = {};
    planList.forEach((p) => { planPriceMap[p.id] = Number(p.price_monthly); });

    // Addon revenue: only enabled tenant_modules with price > 0
    const addonSumMap: Record<string, number> = {};
    const addonCntMap: Record<string, number> = {};
    tenantModList.forEach((tm) => {
      if (tm.is_enabled && Number(tm.price_monthly) > 0) {
        addonSumMap[tm.tenant_id] = (addonSumMap[tm.tenant_id] ?? 0) + Number(tm.price_monthly);
        addonCntMap[tm.tenant_id] = (addonCntMap[tm.tenant_id] ?? 0) + 1;
      }
    });

    const computed: BillingRow[] = tenantList.map((t) => {
      const plan_price  = planPriceMap[t.plan_code] ?? 0;
      const addon_price = addonSumMap[t.id] ?? 0;
      return {
        id: t.id, name: t.name, slug: t.slug, plan_code: t.plan_code, status: t.status,
        plan_price, addon_price, addon_count: addonCntMap[t.id] ?? 0,
        total: plan_price + addon_price,
      };
    });

    setPlans(planList);
    setTenants(tenantList);
    setTenantModules(tenantModList);
    setModules(moduleList);
    setPlanModules(planModList);
    setRows(computed);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      fetchData();
    };
    init();
  }, [navigate, fetchData]);

  // ── Derived metrics ─────────────────────────────────────────────────────────
  const active     = useMemo(() => rows.filter((r) => r.status === 'active'), [rows]);
  const trial      = useMemo(() => rows.filter((r) => r.status === 'trial'), [rows]);
  const suspended  = useMemo(() => rows.filter((r) => r.status === 'suspended' || r.status === 'cancelled'), [rows]);

  const mrrTotal    = useMemo(() => active.reduce((s, r) => s + r.total, 0), [active]);
  const mrrPlans    = useMemo(() => active.reduce((s, r) => s + r.plan_price, 0), [active]);
  const mrrAddons   = useMemo(() => active.reduce((s, r) => s + r.addon_price, 0), [active]);
  const mrrPipeline = useMemo(() => trial.reduce((s, r) => s + r.total, 0), [trial]);

  const planBreakdown = useMemo(() => plans.map((plan) => {
    const planRows     = active.filter((r) => r.plan_code === plan.id);
    const planRevenue  = planRows.reduce((s, r) => s + r.plan_price, 0);
    const addonRevenue = planRows.reduce((s, r) => s + r.addon_price, 0);
    return { plan, count: planRows.length, planRevenue, addonRevenue, total: planRevenue + addonRevenue };
  }), [active, plans]);

  const compositionPct = mrrTotal > 0
    ? { plans: Math.round((mrrPlans / mrrTotal) * 100), addons: Math.round((mrrAddons / mrrTotal) * 100) }
    : { plans: 0, addons: 0 };

  // ── Tenants table filter ────────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let list = [...rows];
    if (search) list = list.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()) || r.slug.includes(search.toLowerCase()));
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    list.sort((a, b) => sortBy === 'total' ? b.total - a.total : sortBy === 'plan' ? a.plan_code.localeCompare(b.plan_code) : a.name.localeCompare(b.name));
    return list;
  }, [rows, search, statusFilter, sortBy]);

  const displayedActive  = displayed.filter((r) => ACTIVE_STATUSES.has(r.status));
  const displayedTotal   = displayedActive.reduce((s, r) => s + r.total, 0);

  // ── Change Plan ─────────────────────────────────────────────────────────────
  const handleChangePlan = async (row: BillingRow) => {
    const inputOptions: Record<string, string> = {};
    plans.forEach((p) => { inputOptions[p.id] = `${p.name} ($${Number(p.price_monthly)}/mes)`; });

    const { value: newPlan } = await Swal.fire({
      title: `Cambiar plan — ${row.name}`,
      input: 'select',
      inputOptions,
      inputValue: row.plan_code,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      ...SW,
    });

    if (!newPlan || newPlan === row.plan_code) return;

    const planDef = plans.find((p) => p.id === newPlan);
    if (!planDef) return;

    // 1) Update saas_tenants
    const { error: tenantErr } = await supabase.from('saas_tenants').update({
      plan_code:           newPlan,
      max_users:           planDef.max_users,
      max_active_contracts: planDef.max_contracts,
      storage_quota_mb:    planDef.storage_mb,
      updated_at:          new Date().toISOString(),
    }).eq('id', row.id);

    if (tenantErr) {
      Swal.fire({ title: 'Error', text: tenantErr.message, icon: 'error', ...SW });
      return;
    }

    // 2) Sync tenant_modules: enable modules included in new plan, disable those not included
    const planMods = planModules.filter((pm) => pm.plan_id === newPlan && pm.is_included);
    const planModIds = new Set(planMods.map((pm) => pm.module_id));

    // Get existing tenant_module rows for this tenant
    const { data: existingMods } = await supabase
      .from('tenant_modules')
      .select('id, module_id, is_enabled, source')
      .eq('tenant_id', row.id);

    const existing = (existingMods ?? []) as { id: string; module_id: string; is_enabled: boolean; source: string }[];
    const existingMap: Record<string, { id: string; is_enabled: boolean; source: string }> = {};
    existing.forEach((e) => { existingMap[e.module_id] = e; });

    // Upsert: enable plan-included modules (source='plan'), disable removed ones (unless manual addon)
    const upserts = modules.map((mod) => {
      const existRow = existingMap[mod.id];
      const inNewPlan = planModIds.has(mod.id);

      // If manual addon, never auto-disable
      if (existRow?.source === 'manual' && existRow?.is_enabled) {
        return null; // skip — keep as is
      }

      return {
        tenant_id:     row.id,
        module_id:     mod.id,
        is_enabled:    inNewPlan,
        source:        inNewPlan ? 'plan' : (existRow?.source ?? 'plan'),
        price_monthly: 0,
      };
    }).filter(Boolean);

    if (upserts.length > 0) {
      await supabase.from('tenant_modules').upsert(upserts, { onConflict: 'tenant_id,module_id' });
    }

    await fetchData();
    Swal.fire({ title: `Plan cambiado a ${planDef.name}`, icon: 'success', timer: 1400, showConfirmButton: false, ...SW });
  };

  // ── Toggle Tenant Status ────────────────────────────────────────────────────
  const handleToggleStatus = async (row: BillingRow) => {
    const action = STATUS_ACTIONS.find((a) => a.status === row.status);
    if (!action) return;

    const { isConfirmed } = await Swal.fire({
      title: `${action.label} — ${row.name}`,
      text:  `El tenant pasará a estado "${action.next}". ¿Confirmar?`,
      showCancelButton: true,
      confirmButtonText: action.label,
      cancelButtonText: 'Cancelar',
      icon: action.next === 'suspended' ? 'warning' : 'question',
      ...SW,
    });
    if (!isConfirmed) return;

    const { error } = await supabase.from('saas_tenants').update({
      status: action.next,
      updated_at: new Date().toISOString(),
    }).eq('id', row.id);

    if (error) {
      Swal.fire({ title: 'Error', text: error.message, icon: 'error', ...SW });
      return;
    }

    await fetchData();
    Swal.fire({ title: `Tenant ${action.next}`, icon: 'success', timer: 1200, showConfirmButton: false, ...SW });
  };

  // ── Stat Card ────────────────────────────────────────────────────────────────
  const StatCard = ({
    label, value, sub, icon, colorText, colorBg, border,
  }: {
    label: string; value: string; sub: string; icon: string;
    colorText: string; colorBg: string; border: string;
  }) => (
    <div className={`bg-white ${border} rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${colorBg} ${colorText}`}>
          <i className={`${icon} text-sm`} />
        </span>
        <p className="text-slate-500 text-xs">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${colorText}`}>{value}</p>
      <p className="text-slate-400 text-[10px] mt-1">{sub}</p>
    </div>
  );

  // ── Tab nav ───────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview'      as TabKey, label: 'Overview',        icon: 'ri-pie-chart-line' },
    { id: 'tenants'       as TabKey, label: 'Tenants Billing', icon: 'ri-building-2-line' },
    { id: 'plan-modules'  as TabKey, label: 'Plan Modules',    icon: 'ri-layout-grid-line' },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <SuperAdminLayout>
      <SAHeader title="Billing" subtitle="MRR real, gestión de planes y módulos" />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1.5 mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                tab === t.id ? 'bg-[#0f172a] text-white' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${t.icon} text-sm`} /></span>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <LoadingState /> : (
          <>
            {/* ══════════════════════════════════════════════════
                TAB 1 — OVERVIEW
            ══════════════════════════════════════════════════ */}
            {tab === 'overview' && (
              <div className="space-y-6">

                {/* MRR Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard
                    label="Total Tenants" value={String(rows.length)}
                    sub={`${active.length} activos · ${trial.length} trial · ${suspended.length} suspendidos`}
                    icon="ri-building-2-line" colorText="text-slate-700" colorBg="bg-slate-100" border="border border-slate-200"
                  />
                  <StatCard
                    label="MRR Planes" value={`$${mrrPlans.toLocaleString()}`}
                    sub={`${active.length} suscripciones activas`}
                    icon="ri-vip-crown-line" colorText="text-[#a6853e]" colorBg="bg-[#C8A96E]/10" border="border border-[#C8A96E]/30"
                  />
                  <StatCard
                    label="MRR Add-ons" value={`$${mrrAddons.toLocaleString()}`}
                    sub={`${active.reduce((s, r) => s + r.addon_count, 0)} módulos extra activos`}
                    icon="ri-puzzle-line" colorText="text-amber-600" colorBg="bg-amber-50" border="border border-amber-200"
                  />
                  <StatCard
                    label="Total MRR" value={`$${mrrTotal.toLocaleString()}`}
                    sub={mrrPipeline > 0 ? `$${mrrPipeline.toLocaleString()} en pipeline (trial)` : 'Sin pipeline'}
                    icon="ri-line-chart-line" colorText="text-emerald-600" colorBg="bg-emerald-50" border="border border-emerald-200"
                  />
                </div>

                {/* MRR Composition bar */}
                {mrrTotal > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Composición del MRR</p>
                      <p className="text-slate-400 text-xs">${mrrTotal.toLocaleString()}/mes</p>
                    </div>
                    <div className="h-3 w-full rounded-full overflow-hidden flex">
                      <div className="h-full bg-[#C8A96E] transition-all" style={{ width: `${compositionPct.plans}%` }} />
                      <div className="h-full bg-amber-300 transition-all" style={{ width: `${compositionPct.addons}%` }} />
                      <div className="h-full bg-slate-100 flex-1" />
                    </div>
                    <div className="flex items-center gap-5 mt-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#C8A96E]" />
                        <span className="text-slate-600 text-xs">Planes <strong className="text-slate-800">{compositionPct.plans}%</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                        <span className="text-slate-600 text-xs">Add-ons <strong className="text-slate-800">{compositionPct.addons}%</strong></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Plan breakdown */}
                <div>
                  <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3">Revenue por plan</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {planBreakdown.map((pb) => (
                      <div key={pb.plan.id} className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${PLAN_BADGE[pb.plan.id] ?? 'bg-slate-100 text-slate-400'}`}>
                              {pb.plan.name}
                            </span>
                          </div>
                          <span className="text-slate-400 text-xs">{pb.count} tenants</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Base (${Number(pb.plan.price_monthly)}/mes)</span>
                            <span className="text-slate-700 text-sm font-semibold">${pb.planRevenue.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-xs">Add-ons</span>
                            <span className="text-amber-600 text-sm font-semibold">${pb.addonRevenue.toLocaleString()}</span>
                          </div>
                          <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
                            <span className="text-slate-500 text-xs font-medium">Total</span>
                            <span className="text-slate-900 text-base font-bold">${pb.total.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tenant status summary */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Activos', count: active.length, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'ri-checkbox-circle-line' },
                    { label: 'Trial',   count: trial.length,  color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   icon: 'ri-timer-line' },
                    { label: 'Suspendidos / Cancelados', count: suspended.length, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', icon: 'ri-pause-circle-line' },
                  ].map((item) => (
                    <div key={item.label} className={`bg-white border ${item.border} rounded-xl p-4 flex items-center gap-3`}>
                      <span className={`w-9 h-9 flex items-center justify-center rounded-xl ${item.bg} ${item.color} shrink-0`}>
                        <i className={`${item.icon} text-lg`} />
                      </span>
                      <div>
                        <p className={`text-2xl font-bold ${item.color}`}>{item.count}</p>
                        <p className="text-slate-500 text-xs">{item.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pipeline note */}
                {trial.length > 0 && (
                  <div className="bg-[#C8A96E]/10 border border-[#C8A96E]/25 rounded-xl p-4 flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-[#a6853e] shrink-0 mt-0.5"><i className="ri-timer-line text-sm" /></span>
                    <div>
                      <p className="text-[#a6853e] text-sm font-semibold">Pipeline de conversión</p>
                      <p className="text-slate-600 text-xs mt-0.5">
                        {trial.length} tenant{trial.length !== 1 ? 's' : ''} en trial con potencial de{' '}
                        <strong>${mrrPipeline.toLocaleString()}/mes</strong> si convierten a plan pago.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB 2 — TENANTS BILLING
            ══════════════════════════════════════════════════ */}
            {tab === 'tenants' && (
              <div className="space-y-4">

                {/* Filters */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                        <i className="ri-search-line text-xs" />
                      </span>
                      <input
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar tenant…"
                        className="bg-white border border-slate-200 rounded-lg pl-7 pr-3 py-2 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-[#C8A96E]/60 w-52"
                      />
                    </div>
                    <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-lg p-0.5">
                      {['all', 'active', 'trial', 'suspended', 'cancelled'].map((s) => (
                        <button
                          key={s} onClick={() => setStatusFilter(s)}
                          className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${
                            statusFilter === s ? 'bg-[#0f172a] text-white' : 'text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          {s === 'all' ? 'Todos' : s}
                        </button>
                      ))}
                    </div>
                    <select
                      value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                      className="bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-slate-500 text-xs focus:outline-none cursor-pointer"
                    >
                      <option value="total">Mayor ingreso</option>
                      <option value="name">Nombre A-Z</option>
                      <option value="plan">Plan</option>
                    </select>
                  </div>
                  <span className="text-slate-400 text-xs whitespace-nowrap">
                    {displayed.length} tenants · <strong className="text-slate-600">${displayedTotal.toLocaleString()}</strong> MRR filtrado
                  </span>
                </div>

                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left text-slate-500 text-xs font-semibold uppercase tracking-wider py-3.5 px-5">Tenant</th>
                          <th className="text-left text-slate-500 text-xs font-semibold uppercase tracking-wider py-3.5 px-4">Plan</th>
                          <th className="text-right text-slate-500 text-xs font-semibold uppercase tracking-wider py-3.5 px-4">Plan Price</th>
                          <th className="text-right text-slate-500 text-xs font-semibold uppercase tracking-wider py-3.5 px-4">Add-ons</th>
                          <th className="text-right text-slate-500 text-xs font-semibold uppercase tracking-wider py-3.5 px-4">Total/mes</th>
                          <th className="text-left text-slate-500 text-xs font-semibold uppercase tracking-wider py-3.5 px-4">Status</th>
                          <th className="py-3.5 px-4"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {displayed.map((row) => {
                          const statusAction = STATUS_ACTIONS.find((a) => a.status === row.status);
                          return (
                            <tr key={row.id} className={`hover:bg-slate-50/70 transition-colors ${!ACTIVE_STATUSES.has(row.status) ? 'opacity-60' : ''}`}>
                              {/* Tenant */}
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-[#C8A96E]/15 text-[#a6853e] text-xs font-bold flex items-center justify-center shrink-0">
                                    {row.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <Link to={`/sa/tenants/${row.id}`} className="text-slate-900 text-sm font-medium hover:text-[#C8A96E] transition-colors">
                                      {row.name}
                                    </Link>
                                    <p className="text-slate-400 text-xs font-mono">{row.slug}</p>
                                  </div>
                                </div>
                              </td>

                              {/* Plan */}
                              <td className="px-4 py-3.5">
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${PLAN_BADGE[row.plan_code] ?? 'bg-slate-100 text-slate-400'}`}>
                                  {row.plan_code}
                                </span>
                              </td>

                              {/* Plan Price */}
                              <td className="px-4 py-3.5 text-right">
                                <span className="text-slate-600 text-sm">
                                  {row.plan_price === 0
                                    ? <span className="text-slate-300">—</span>
                                    : `$${row.plan_price}`}
                                </span>
                              </td>

                              {/* Add-ons */}
                              <td className="px-4 py-3.5 text-right">
                                {row.addon_price > 0 ? (
                                  <button
                                    onClick={() => setAddonModal(row)}
                                    className="text-right hover:opacity-80 cursor-pointer"
                                  >
                                    <span className="text-amber-600 text-sm font-medium">${row.addon_price}</span>
                                    <p className="text-slate-400 text-[10px]">{row.addon_count} add-ons →</p>
                                  </button>
                                ) : (
                                  <span className="text-slate-300 text-sm">—</span>
                                )}
                              </td>

                              {/* Total */}
                              <td className="px-4 py-3.5 text-right">
                                {row.total > 0 ? (
                                  <span className={`text-sm font-bold ${ACTIVE_STATUSES.has(row.status) ? 'text-slate-900' : 'text-slate-400'}`}>
                                    ${row.total}
                                  </span>
                                ) : (
                                  <span className="text-slate-300 text-sm">$0</span>
                                )}
                              </td>

                              {/* Status */}
                              <td className="px-4 py-3.5">
                                <StatusBadge status={row.status} />
                              </td>

                              {/* Actions */}
                              <td className="px-4 py-3.5">
                                <div className="flex items-center justify-end gap-1">
                                  {/* Change plan */}
                                  <button
                                    onClick={() => handleChangePlan(row)}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#a6853e] hover:bg-[#C8A96E]/10 transition-colors cursor-pointer"
                                    title="Cambiar plan"
                                  >
                                    <i className="ri-vip-crown-line text-sm" />
                                  </button>

                                  {/* Toggle status */}
                                  {statusAction && (
                                    <button
                                      onClick={() => handleToggleStatus(row)}
                                      className={`w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 ${statusAction.color} transition-colors cursor-pointer`}
                                      title={statusAction.label}
                                    >
                                      <i className={`${statusAction.icon} text-sm`} />
                                    </button>
                                  )}

                                  {/* View addons */}
                                  <button
                                    onClick={() => setAddonModal(row)}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer"
                                    title="Ver add-ons"
                                  >
                                    <i className="ri-puzzle-line text-sm" />
                                  </button>

                                  {/* Detail link */}
                                  <Link
                                    to={`/sa/tenants/${row.id}`}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                    title="Ver detalle"
                                  >
                                    <i className="ri-external-link-line text-xs" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}

                        {displayed.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-14 text-center">
                              <div className="flex flex-col items-center gap-3">
                                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-300">
                                  <i className="ri-building-line text-xl" />
                                </div>
                                <p className="text-slate-400 text-sm">
                                  {rows.length === 0 ? 'Sin tenants registrados' : 'Sin coincidencias para los filtros actuales'}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>

                      {/* Footer totals */}
                      {displayed.length > 0 && (
                        <tfoot>
                          <tr className="border-t border-slate-200 bg-slate-50">
                            <td className="px-5 py-3 text-slate-500 text-xs font-medium" colSpan={2}>
                              {displayed.length} tenants · {displayedActive.length} activos/trial
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600 text-sm font-semibold">
                              ${displayedActive.reduce((s, r) => s + r.plan_price, 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-amber-600 text-sm font-semibold">
                              ${displayedActive.reduce((s, r) => s + r.addon_price, 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-emerald-600 text-sm font-bold">
                              ${displayedTotal.toLocaleString()}
                            </td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Info note */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-2.5">
                  <i className="ri-information-line text-slate-400 text-sm mt-0.5" />
                  <p className="text-slate-400 text-xs leading-relaxed">
                    MRR = <strong className="text-slate-600">precio del plan</strong> + <strong className="text-slate-600">add-ons habilitados</strong> (tenant_modules con price_monthly &gt; 0).
                    Solo tenants con status <strong className="text-slate-600">active</strong> cuentan al MRR total.
                    Haz clic en el icono <i className="ri-puzzle-line text-[11px]" /> para ver add-ons por tenant.
                  </p>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB 3 — PLAN MODULES
            ══════════════════════════════════════════════════ */}
            {tab === 'plan-modules' && (
              <PlanModulesMatrix
                modules={modules}
                plans={plans}
                planModules={planModules}
                onRefresh={fetchData}
              />
            )}
          </>
        )}
      </div>

      {/* Addon detail modal */}
      {addonModal && (
        <AddonDetailModal
          tenant={addonModal}
          modules={modules}
          tenantModules={tenantModules}
          onClose={() => setAddonModal(null)}
        />
      )}
    </SuperAdminLayout>
  );
}